// Query keys — the encoding of HMS_V4_API_CONTRACT.md §5.
//
// The bug this exists to prevent: some data is tenant-wide and some is
// branch-scoped. Put branchId in the wrong set of keys and you get one of two
// silent failures — a doctor switches branch and still sees the previous
// branch's queue (branchId missing from a scoped key), or the whole patient
// cache is thrown away on every switch for data that is identical everywhere
// (branchId present on a tenant-wide key).
//
// Documenting the rule was not enough, so it is structural instead: there are
// two factories, `tenantKey` and `branchKey`, and branchKey injects the active
// branch itself. A developer cannot forget to include it, because they never
// write it.
//
// The rule for deciding which: does the backend route call getBranchOrThrow()?
// If yes it is branch-scoped. If it is reached by :uhid and returns clinical
// history, it is tenant-wide — the branch model's stated intent is that a
// patient's record is visible at every branch.

import type { QueryClient } from '@tanstack/react-query';
import { getActiveBranchId } from './api/session';

/** Marks the branch-scoped half of the cache so it can be invalidated wholesale. */
const BRANCH_SCOPE = 'branch' as const;
const TENANT_SCOPE = 'tenant' as const;

type KeyPart = string | number | boolean | null | undefined | Record<string, unknown>;

/**
 * A key for data that is identical at every branch — patients, clinical
 * history, documents, config. Never carries a branch id.
 */
export function tenantKey(...parts: KeyPart[]): readonly unknown[] {
  return [TENANT_SCOPE, ...parts] as const;
}

/**
 * A key for data belonging to one physical site — visits, queue, bills,
 * inventory, admissions, appointment slots.
 *
 * The active branch id is read here rather than passed in, so it is impossible
 * to omit. It sits immediately after the scope marker so `[BRANCH_SCOPE]`
 * remains a valid invalidation prefix for every branch-scoped query at once.
 */
export function branchKey(...parts: KeyPart[]): readonly unknown[] {
  return [BRANCH_SCOPE, getActiveBranchId(), ...parts] as const;
}

/**
 * Called when the user switches branch. Evicts every branch-scoped cache
 * entry and leaves tenant-wide data (patient roster, clinical history,
 * documents) warm — that data did not change, and refetching it would make
 * every branch switch feel like a page load.
 *
 * ── removeQueries, not invalidateQueries — this is deliberate, not a stylistic choice ──
 *
 * branchKey() embeds the CURRENT branch id at the moment a component renders.
 * getActiveBranchId() is written to localStorage synchronously, before this
 * function runs — but a mounted component's queryKey argument is a closure
 * value from ITS last render, so at the instant this function is called,
 * every currently-observed branch query is still keyed on the OLD branch id.
 *
 * invalidateQueries refetches active queries in place, under their EXISTING
 * key. The query function itself reads getActiveBranchId() fresh (see
 * api/client.ts's buildHeaders) — so that refetch would hit the NEW branch's
 * API, but store the response under the OLD branch's cache key. The result:
 * silently wrong data cached under a key some other, not-yet-reconciled part
 * of the UI might still read from.
 *
 * removeQueries sidesteps this entirely by evicting rather than refetching —
 * no request is issued against a key that is about to become unreachable.
 * Every screen built against this contract keys its branch-scoped hooks off
 * useAuth()'s `branchVersion` (see e.g. features/queue/useTodayQueue.ts) —
 * that is the mechanism that forces a re-render and a freshly-computed,
 * correctly-scoped key. This function is a memory-hygiene backstop for that
 * convention, not the primary refetch trigger.
 */
export function invalidateBranchScoped(qc: QueryClient): void {
  qc.removeQueries({ queryKey: [BRANCH_SCOPE] });
}

// ── Domain keys ──────────────────────────────────────────────────
//
// Grouped by scope so the choice is visible at the point of definition rather
// than inferred at each call site.

export const keys = {
  // ---- Tenant-wide -------------------------------------------------------
  patients: {
    all: () => tenantKey('patients'),
    list: (params?: Record<string, unknown>) => tenantKey('patients', 'list', params ?? {}),
    search: (q: string) => tenantKey('patients', 'search', q),
    detail: (uhid: string) => tenantKey('patient', uhid),
    timeline: (uhid: string) => tenantKey('patient', uhid, 'timeline'),
    summary: (uhid: string) => tenantKey('patient', uhid, 'summary'),
    documents: (uhid: string) => tenantKey('patient', uhid, 'documents'),
    erasureRequests: (uhid: string) => tenantKey('patient', uhid, 'erasure-requests'),
    /** Legal audit trail — tenant-wide like the rest of a patient's record. */
    audit: (uhid: string, kind: string) => tenantKey('patient', uhid, 'audit', kind),
    /** Deceased status + its audit history — patient-level, so tenant-wide. */
    deceased: (uhid: string) => tenantKey('patient', uhid, 'deceased'),
    /**
     * Consent is tenant-wide, not branch-scoped: a consent given at one branch
     * is the same patient's consent everywhere (see the module comment above —
     * reached by :uhid, returns the patient's own record).
     */
    consent: (uhid: string) => tenantKey('patient', uhid, 'consent'),
    consentHistory: (uhid: string) => tenantKey('patient', uhid, 'consent', 'history'),
  },

  /** Clinical lists hanging off a patient. */
  clinical: {
    /** The caller's own clinical-reference questions (G1) — per user, tenant-wide. */
    referenceHistory: () => tenantKey('clinical-references', 'history'),
    referenceStatus: () => tenantKey('clinical-references', 'status'),
    /** Web Search References — the caller's own searches, and whether they can search. */
    webSearchHistory: () => tenantKey('clinical-web-search', 'history'),
    webSearchStatus: () => tenantKey('clinical-web-search', 'status'),
    allergies: (uhid: string, includeInactive = false) =>
      tenantKey('patient', uhid, 'allergies', includeInactive),
    diagnoses: (uhid: string) => tenantKey('patient', uhid, 'diagnoses'),
    procedures: (uhid: string) => tenantKey('patient', uhid, 'procedures'),
    medications: (uhid: string) => tenantKey('patient', uhid, 'medications'),
    /** Encounter diagnoses across every visit — history, so tenant-wide. */
    encounterDiagnosesForPatient: (uhid: string) =>
      tenantKey('patient', uhid, 'encounter-diagnoses'),
    prescriptionsForPatient: (uhid: string) => tenantKey('patient', uhid, 'prescriptions'),
    consultations: (patientId: number) => tenantKey('patient', patientId, 'consultations'),
    billsForPatient: (patientId: number) => tenantKey('patient', patientId, 'bills'),
  },

  /**
   * GET /api/users?role=X — tenant-wide (users.js applies no branch filter:
   * a doctor can work at more than one branch, see migration_branches.sql).
   * Deliberately separate from config.users() below, which is the FULL admin
   * roster (a different query, different response shape) — same key would
   * mean two unrelated queries silently overwriting one cache entry.
   */
  staff: {
    byRole: (role: string) => tenantKey('staff', role),
  },

  /**
   * Vendors and purchase orders — tenant-wide (neither route calls
   * getBranchOrThrow(): a vendor relationship and a PO are not tied to one
   * physical site the way received stock or a dispensed sale are).
   */
  procurement: {
    vendors: () => tenantKey('procurement', 'vendors'),
    purchaseOrders: () => tenantKey('procurement', 'purchase-orders'),
    /** Single PO detail (items joined to medicines) — same tenant-wide reasoning as the list above. */
    purchaseOrder: (id: number) => tenantKey('procurement', 'purchase-order', id),
  },

  /**
   * The pharmacy counter's customer lookup and the loyalty programme —
   * TENANT-wide, both of them, and for the same reason: a patient is a
   * patient at every branch (that is the branch model's stated intent), and a
   * points balance follows the person, not the till they last used. Keying
   * either by branch would throw the cache away on a branch switch for data
   * that is identical everywhere — and worse, would let two branches hold
   * different cached balances for one customer.
   *
   * The SALES those customers make are branch-scoped instead (see
   * pharmacy.sales below): stock leaves one physical shelf.
   */
  counter: {
    customerSearch: (q: string) => tenantKey('counter', 'customers', q),
    customer: (patientId: number) => tenantKey('counter', 'customer', patientId),
    purchases: (patientId: number) => tenantKey('counter', 'customer', patientId, 'purchases'),
  },

  /**
   * Medicine Borrow & Return POLICY — tenant-wide, like loyalty settings: one
   * hospital, one lending policy. The records themselves are branch-scoped
   * (keys.pharmacy.borrow*).
   */
  borrowSettings: () => tenantKey('pharmacy-borrow', 'settings'),
  /** The admin read of the same row — its own key because a different endpoint (and grant) serves it. */
  borrowSettingsAdmin: () => tenantKey('pharmacy-borrow', 'settings', 'admin'),

  /**
   * The near-expiry window + later-batch reason list — tenant-wide, one
   * hospital, one window (pharmacy_dispensing_settings). The Near-Expiry
   * report built from it is branch-scoped (keys.pharmacy.nearExpiry).
   */
  dispensingSettings: () => tenantKey('pharmacy-dispensing', 'settings'),
  dispensingSettingsAdmin: () => tenantKey('pharmacy-dispensing', 'settings', 'admin'),

  loyalty: {
    settings: () => tenantKey('loyalty', 'settings'),
    balance: (patientId: number) => tenantKey('loyalty', 'balance', patientId),
    history: (patientId: number) => tenantKey('loyalty', 'history', patientId),
  },

  /**
   * Laboratory MASTERS — tenant-wide, deliberately. A reference range, a test
   * price and a container are hospital policy: diverging them per branch is
   * how the same blood gets two different answers depending on which door the
   * patient walked in (see migration_laboratory.sql's scoping note). The lab's
   * OPERATIONAL data — orders, samples, results, reports — is branch-scoped
   * instead, under `laboratory` below.
   */
  labMasters: {
    all: () => tenantKey('lab-masters'),
    departments: () => tenantKey('lab-masters', 'departments'),
    testCategories: () => tenantKey('lab-masters', 'test-categories'),
    sampleTypes: () => tenantKey('lab-masters', 'sample-types'),
    containers: () => tenantKey('lab-masters', 'containers'),
    tests: (params?: Record<string, unknown>) => tenantKey('lab-masters', 'tests', params ?? {}),
    test: (id: number) => tenantKey('lab-masters', 'test', id),
    referenceRanges: (parameterId: number) => tenantKey('lab-masters', 'parameter', parameterId, 'ranges'),
    packages: () => tenantKey('lab-masters', 'packages'),
    referenceLabs: () => tenantKey('lab-masters', 'reference-labs'),
  },

  /**
   * A patient's laboratory history and parameter trends — tenant-wide for the
   * same reason clinical.prescriptionsForPatient is: a patient's record is
   * visible at every branch of the same hospital, and a result taken at one
   * site is part of the record read at another.
   */
  patientLab: {
    history: (uhid: string) => tenantKey('patient', uhid, 'lab-orders'),
    trend: (uhid: string, parameterCode: string) => tenantKey('patient', uhid, 'lab-trend', parameterCode),
    /** Outside-laboratory tests in the patient's record — tenant-wide like the rest of their history. */
    outsourced: (uhid: string) => tenantKey('patient', uhid, 'lab-outsourced'),
  },

  /** Admin/config tables — small, tenant-wide, rarely changing. */
  config: {
    users: () => tenantKey('users'),
    roles: () => tenantKey('roles'),
    branches: () => tenantKey('branches'),
    specialties: () => tenantKey('specialties'),
    ailmentTemplates: () => tenantKey('ailment-templates'),
    branding: () => tenantKey('branding'),
    intakeConfig: () => tenantKey('intake-config'),
    /** Vitals symptom fields — Admin Panel -> Clinical -> Vitals Form Customization. */
    symptomForm: (includeDisabled = false) => tenantKey('symptom-form', includeDisabled),
    /** Versioned consent wording — Admin Panel -> Clinical Setup -> Consent Forms. */
    consentTemplates: () => tenantKey('consent-templates'),
    /** GET /api/ai-settings — read by useAiAvailability() from any screen with an AI control. */
    aiSettings: () => tenantKey('ai-settings'),
  },

  /**
   * Self-service view over backend/jobs/'s platform.job_runs ledger
   * (routes/admin-job-runs.js). Tenant-wide despite the row physically
   * living in the platform schema: from this app's perspective it's "my
   * hospital's own job history", and jobs run per-tenant, not per-branch —
   * see docs/HMS_V4_BACKGROUND_JOBS.md.
   */
  admin: {
    /** The Admin home's "needs attention" items (routes/admin-attention.js). Tenant-wide. */
    attention: () => tenantKey('admin', 'attention'),
    jobRuns: (params?: Record<string, unknown>) => tenantKey('admin', 'job-runs', params ?? {}),
    jobDefinitions: () => tenantKey('admin', 'job-runs', 'jobs'),

    /**
     * SMS Gateway (routes/admin-sms-gateway.js). Tenant-wide: one hospital has
     * one MSG91 account and one set of DLT-approved wordings — a wording is
     * registered with the telecom regulator against the hospital, not against
     * a site, so branch-scoping these would invalidate a cache on every branch
     * switch for data that is identical everywhere.
     *
     * The delivery log is tenant-wide for the same reason its table is: it is
     * the single answer to "was the patient told?", and a patient may have
     * been messaged from any branch.
     */
    smsGateway: () => tenantKey('admin', 'sms-gateway'),
    smsTemplates: () => tenantKey('admin', 'sms-gateway', 'templates'),
    smsLog: (params?: Record<string, unknown>) => tenantKey('admin', 'sms-gateway', 'log', params ?? {}),
    /** The messages the app sends by itself (static, from code) — the Notifications tab. */
    notificationEvents: () => tenantKey('admin', 'sms-gateway', 'events'),
    aiCallLog: (params?: Record<string, unknown>) => tenantKey('admin', 'ai-call-log', params ?? {}),
    aiCallLogDetail: (id: number) => tenantKey('admin', 'ai-call-log', 'detail', id),
    aiCallLogSummary: () => tenantKey('admin', 'ai-call-log', 'summary'),
    // AI Activity (G3) — tenant-wide, like the call log.
    aiActivitySummary: (window: string) => tenantKey('admin', 'ai-activity', 'summary', window),
    aiActivityTasks: (params: Record<string, unknown>) => tenantKey('admin', 'ai-activity', 'tasks', params),
    aiActivityTask: (id: number) => tenantKey('admin', 'ai-activity', 'task', id),
    aiActivityCopilotGaps: (window: string, show: string) => tenantKey('admin', 'ai-activity', 'copilot-gaps', window, show),
    /** G1 reference library — tenant-wide: a hospital's references apply at every branch. */
    clinicalLibrary: (filter: Record<string, unknown>) => tenantKey('admin', 'clinical-library', 'list', filter),
    clinicalLibraryDoc: (id: number) => tenantKey('admin', 'clinical-library', 'doc', id),
    clinicalLibraryStatus: () => tenantKey('admin', 'clinical-library', 'status'),
    clinicalWebSearchSettings: () => tenantKey('admin', 'clinical-library', 'web-search'),

    /**
     * WhatsApp side of the SMS Gateway screen (routes/admin-whatsapp-gateway.js).
     * Tenant-wide for the same reasons as the SMS keys above: one MSG91 account
     * and one set of Meta-approved templates per hospital. The delivery log is
     * shared with SMS and keyed under smsLog().
     */
    whatsappGateway: () => tenantKey('admin', 'whatsapp-gateway'),
    whatsappTemplates: () => tenantKey('admin', 'whatsapp-gateway', 'templates'),

    /**
     * Data Privacy (routes/data-privacy.js) — the retention policy and the
     * erasure-request review queue are both tenant-wide: a policy commitment
     * and a DPDP request apply to the hospital/patient, not to one branch.
     */
    dataRetention: () => tenantKey('admin', 'data-privacy', 'retention'),
    erasureRequests: (status?: string) => tenantKey('admin', 'data-privacy', 'erasure-requests', status ?? 'all'),
    /** Prefix (no status segment) — for invalidating every status filter's cached list at once. */
    erasureRequestsAll: () => tenantKey('admin', 'data-privacy', 'erasure-requests'),
  },

  // ---- Branch-scoped -----------------------------------------------------
  visits: {
    today: () => branchKey('visits', 'today'),
    detail: (visitCode: string) => branchKey('visit', visitCode),
    /** Diagnoses recorded at one visit — reached through a branch-scoped visit. */
    diagnoses: (visitCode: string) => branchKey('visit', visitCode, 'diagnoses'),
    search: (q: string) => branchKey('visits', 'search', q),
  },

  queue: {
    today: () => branchKey('queue', 'today'),
    /**
     * Queue Prioritization (routes/admin-queue-priority.js, routes/queue.js's
     * /priority-rules). Branch-scoped: each branch has its own settings row.
     */
    priorityRules: () => branchKey('queue', 'priority-rules'),
    prioritySettings: () => branchKey('queue', 'priority-settings'),
    priorityHistory: () => branchKey('queue', 'priority-settings', 'history'),
  },

  appointments: {
    slots: (params?: Record<string, unknown>) => branchKey('appointment-slots', params ?? {}),
    availability: (doctorId?: number) => branchKey('doctor-availability', doctorId ?? null),
  },

  prescriptions: {
    /** The operational list (pharmacy workload) — branch-scoped. */
    list: (params?: Record<string, unknown>) => branchKey('prescriptions', params ?? {}),
    detail: (id: number) => branchKey('prescription', id),
  },

  /** The topbar bell (contract §6k). Branch-scoped: every source is read at the active branch. */
  notifications: {
    feed: () => branchKey('me', 'notifications'),
  },
  /** The caller's own submitted feedback — tenant-wide (a person, not a branch). */
  feedback: {
    mine: () => tenantKey('me', 'feedback'),
  },
  pharmacy: {
    inventory: () => branchKey('pharmacy', 'inventory'),
    /** One product's panel (G10) — branch-scoped because it carries this branch's stock and receipts. */
    product: (id: number) => branchKey('pharmacy', 'product', id),
    /** The stores at this branch + the channel → store map (migration_pharmacy_stores.sql). */
    stores: () => branchKey('pharmacy', 'stores'),
    /** Admin → Pharmacy Stores — same rows, admin endpoint and grant. */
    storesAdmin: () => branchKey('pharmacy', 'stores', 'admin'),
    /** One batch's full receipt/dispense/adjustment ledger — see routes/pharmacy.js's GET /inventory/:id/history. */
    inventoryHistory: (inventoryId: number) => branchKey('pharmacy', 'inventory', inventoryId, 'history'),
    stockAdjustments: () => branchKey('pharmacy', 'stock-adjustments'),
    /** GET /api/pharmacy/near-expiry — this branch's shelf, so branch-scoped. */
    nearExpiryAll: () => branchKey('pharmacy', 'near-expiry'),
    nearExpiry: (selectionsDays: number) => branchKey('pharmacy', 'near-expiry', selectionsDays),
    /** Received stock is tied to the branch that received it, unlike the PO or vendor behind it. */
    goodsReceipts: () => branchKey('pharmacy', 'goods-receipts'),
    /** Polled while the vendor-invoice QR modal is open — the invoice belongs to the receiving branch. */
    vendorInvoiceMobileSession: (token: string) => branchKey('pharmacy', 'vendor-invoice-mobile-session', token),
    /** One uploaded/extracted invoice, resumed after a mobile upload lands or a slow extraction finishes. */
    vendorInvoiceUpload: (id: number) => branchKey('pharmacy', 'vendor-invoice', id),
    /** Statutory dispensing log — a sale happens at a specific branch. */
    scheduleRegister: () => branchKey('pharmacy', 'schedule-register'),

    /** The drug-substitution approval queue — branch-scoped, like every other operational list. */
    substitutionsPending: () => branchKey('pharmacy', 'substitutions', 'pending'),
    substitutionsCount: () => branchKey('pharmacy', 'substitutions', 'count'),

    /**
     * Walk-in counter sales and returns. Branch-scoped: the medicine came off
     * one branch's shelf and the cash went into one branch's till, and
     * routes/pharmacy-outpatient.js filters on getBranchOrThrow() to match.
     */
    sales: (params?: Record<string, unknown>) => branchKey('pharmacy', 'sales', params ?? {}),
    sale: (id: number) => branchKey('pharmacy', 'sale', id),
    returns: (date?: string) => branchKey('pharmacy', 'returns', date ?? null),
    /**
     * A patient's repeat medicines. Branch-scoped despite hanging off a
     * tenant-wide patient, because the response resolves each past purchase to
     * the batch that would be sold TODAY AT THIS BRANCH — the history is
     * hospital-wide, the stock behind it is not.
     */
    repeatMedicines: (patientId: number) => branchKey('pharmacy', 'repeat-medicines', patientId),
    /**
     * The IP Pharmacy counter (routes/ip-pharmacy.js) — branch-scoped like
     * every other till query above: an admission, its active orders and its
     * bills are all looked up within one branch.
     */
    ipAdmissionSearch: (q: string) => branchKey('pharmacy', 'ip-admissions', q),
    ipActiveOrders: (admissionId: number) => branchKey('pharmacy', 'ip-admission', admissionId, 'active-orders'),

    /**
     * Medicine Borrow & Return — branch-scoped throughout: the stock left one
     * branch's shelf and any payment came into one branch's till, and
     * routes/pharmacy-borrow.js filters every read on getBranchOrThrow().
     * `borrowAll()` is the invalidation prefix for every key below.
     */
    /**
     * Stock transfers between this branch's stores — branch-scoped (both
     * stores belong to the branch). `transfersAll()` is the invalidation
     * prefix; a transfer also moves inventory, so callers invalidate
     * keys.pharmacy.inventory() and keys.pharmacy.stores() with it.
     */
    transfersAll: () => branchKey('pharmacy', 'transfers'),
    transfersList: (params?: Record<string, unknown>) => branchKey('pharmacy', 'transfers', 'list', params ?? {}),
    transferDetail: (id: number) => branchKey('pharmacy', 'transfers', 'detail', id),
    transferStock: (storeId: number, q: string) => branchKey('pharmacy', 'transfers', 'stock', storeId, q),
    transferStores: () => branchKey('pharmacy', 'transfers', 'stores'),
    /**
     * Ward medication requests (multi-store Phase 4) — branch-scoped like the
     * admission they belong to. `wardRequestsAll()` is the invalidation prefix
     * for the worklist and every admission's list.
     */
    wardRequestsAll: () => branchKey('pharmacy', 'ward-requests'),
    wardRequests: (admissionId: number) => branchKey('pharmacy', 'ward-requests', 'admission', admissionId),
    ipWorklist: () => branchKey('pharmacy', 'ward-requests', 'worklist'),
    /** Indents and reorder levels sit under the same prefix: a transfer changes both. */
    indents: (params?: Record<string, unknown>) => branchKey('pharmacy', 'transfers', 'indents', params ?? {}),
    indentDetail: (id: number) => branchKey('pharmacy', 'transfers', 'indent', id),
    stockLevels: (storeId: number) => branchKey('pharmacy', 'transfers', 'levels', storeId),

    borrowAll: () => branchKey('pharmacy', 'borrow'),
    borrowList: (params?: Record<string, unknown>) => branchKey('pharmacy', 'borrow', 'list', params ?? {}),
    borrowDetail: (id: number) => branchKey('pharmacy', 'borrow', 'detail', id),
    borrowSummary: () => branchKey('pharmacy', 'borrow', 'summary'),
    borrowReturns: (params?: Record<string, unknown>) => branchKey('pharmacy', 'borrow', 'returns', params ?? {}),
    borrowPayments: (params?: Record<string, unknown>) => branchKey('pharmacy', 'borrow', 'payments', params ?? {}),
    borrowStock: (q: string) => branchKey('pharmacy', 'borrow', 'stock', q),
    borrowHospitals: (q: string) => branchKey('pharmacy', 'borrow', 'hospitals', q),
  },

  billing: {
    list: (params?: Record<string, unknown>) => branchKey('bills', params ?? {}),
    items: (billId: number) => branchKey('bills', billId, 'items'),
    payments: (billId: number) => branchKey('bills', billId, 'payments'),
    creditNotes: (billId: number) => branchKey('bills', billId, 'credit-notes'),
    /** The pending-settlement list across every bill, not one bill's own. */
    creditNoteList: (params?: Record<string, unknown>) => branchKey('bills', 'credit-notes', params ?? {}),
  },

  /**
   * OPD fee schedule. The admin list is tenant-wide (items are hospital-wide,
   * overrides name their own branch); a quote is branch-scoped because the
   * fee that applies depends on the active branch.
   */
  opdFees: {
    admin: () => tenantKey('opd-fees', 'admin'),
    quote: (params: Record<string, unknown>) => branchKey('opd-fees', 'quote', params),
  },

  /**
   * Insurance claims. Branch-scoped — `routes/claims.js` filters every one
   * of these on `getBranchOrThrow()` (a claim carries its own `branch_id`,
   * set from the admission/visit it was opened against), so a branch switch
   * must evict the worklist and any open workspace rather than leave
   * another site's claims on screen.
   */
  claims: {
    list: (params?: Record<string, unknown>) => branchKey('claims', 'list', params ?? {}),
    detail: (id: number) => branchKey('claims', id),
    runningBill: (id: number) => branchKey('claims', id, 'running-bill'),
    forAdmission: (admissionId: number) => branchKey('claims', 'for-admission', admissionId),
    invoices: (id: number) => branchKey('claims', id, 'invoices'),
    /** Per-form generation status + live missing fields (GET /claims/:id/forms). */
    forms: (id: number) => branchKey('claims', id, 'forms'),
    /** Receivables & reports (Phase 7, GET /claims/reports/:report). */
    report: (report: string, params: Record<string, unknown>) => branchKey('claims', 'reports', report, params),
  },

  ip: {
    wards: () => branchKey('ip', 'wards'),
    admissions: (params?: Record<string, unknown>) => branchKey('ip', 'admissions', params ?? {}),

    /**
     * The IP Case Sheet. Branch-scoped, because everything here hangs off an
     * admission and an admission belongs to one site — so a branch switch must
     * evict the whole subtree rather than leave another branch's ward round on
     * screen.
     *
     * Keyed under one `caseSheet(admissionId)` prefix so closing a patient can
     * drop every section at once, instead of enumerating twelve keys.
     */
    caseSheet: (admissionId: number) => branchKey('ip', 'admission', admissionId),
    caseSheetSection: (admissionId: number, section: string, params?: Record<string, unknown>) =>
      branchKey('ip', 'admission', admissionId, section, params ?? {}),
    /** Deterministic alert configuration — tenant-wide clinical policy, not per-site. */
    alertRules: () => tenantKey('ip', 'alert-rules'),
    /** Insurance Claims Phase 4.5.1 — coverage/payer rows for one admission
     * (`ip_admission_payers`), branch-scoped through the admission the same
     * way `caseSheet` is. `payerSchemes` is the fixed category master
     * (self/insurance/pmjay/…) — tenant-wide, like `alertRules`. */
    payers: (admissionId: number) => branchKey('ip', 'admission', admissionId, 'payers'),
    payerSchemes: () => tenantKey('ip', 'payer-schemes'),
    /** Deposits taken during the stay (admission_deposits) — admission-scoped, same as `payers`. */
    deposits: (admissionId: number) => branchKey('ip', 'admission', admissionId, 'deposits'),
    runningBill: (admissionId: number) => branchKey('ip', 'admission', admissionId, 'running-bill'),
    /** The plain `GET /ip/admissions/:id` read (age/gender/ward/bed/admitted_at)
     * — distinct from `caseSheet`'s full case-sheet payload; the claim
     * workspace header only needs this smaller, already-existing read. */
    admissionSummary: (admissionId: number) => branchKey('ip', 'admission', admissionId, 'summary'),
    /** Insurance Claims Phase 6.1-6.3 — the admission_packages applied to
     * one admission (admission-scoped, same as `payers`/`deposits`). */
    package: (admissionId: number) => branchKey('ip', 'admission', admissionId, 'package'),
  },

  /**
   * Laboratory OPERATIONAL data. Branch-scoped: an order is raised at a site,
   * a tube is drawn there and sits in that laboratory's rack, and
   * routes/laboratory/* filters every one of these on getBranchOrThrow() to
   * match. A branch switch must evict them rather than leave another site's
   * work queue on screen.
   *
   * Keyed under one `order(id)` prefix so closing an order drops its detail,
   * its result sheets and its report together instead of enumerating them.
   */
  laboratory: {
    dashboard: () => branchKey('lab', 'dashboard'),
    orders: (params?: Record<string, unknown>) => branchKey('lab', 'orders', params ?? {}),
    order: (id: number) => branchKey('lab', 'order', id),
    samples: (params?: Record<string, unknown>) => branchKey('lab', 'samples', params ?? {}),
    sample: (id: number) => branchKey('lab', 'sample', id),
    sampleByBarcode: (barcode: string) => branchKey('lab', 'sample-barcode', barcode),
    results: (params?: Record<string, unknown>) => branchKey('lab', 'results', params ?? {}),
    /** Keyed on the ORDER ITEM — the sheet exists before any result row does. */
    resultSheet: (orderItemId: number) => branchKey('lab', 'result-sheet', orderItemId),
    critical: (params?: Record<string, unknown>) => branchKey('lab', 'critical', params ?? {}),
    report: (orderId: number) => branchKey('lab', 'report', orderId),
    /** The Outside / Reference Lab worklist and one test's detail — branch-scoped through the order. */
    outsourced: (params?: Record<string, unknown>) => branchKey('lab', 'outsourced', params ?? {}),
    outsourcedItem: (orderItemId: number) => branchKey('lab', 'outsourced-item', orderItemId),
    tat: (from?: string, to?: string) => branchKey('lab', 'mis', 'tat', from ?? null, to ?? null),
    testWise: (from?: string, to?: string) => branchKey('lab', 'mis', 'test-wise', from ?? null, to ?? null),
    revenue: (from?: string, to?: string) => branchKey('lab', 'mis', 'revenue', from ?? null, to ?? null),
    rejections: (from?: string, to?: string) => branchKey('lab', 'mis', 'rejections', from ?? null, to ?? null),
  },

  /**
   * Emergency / Casualty. Branch-scoped: an ED encounter belongs to the site
   * the patient arrived at (routes/emergency/* call getBranchOrThrow, and the
   * guard 404s another branch's encounter). The policy masters behind
   * `meta` (triage scale, MLC profile, payer schemes) are tenant-wide, but
   * meta also carries this branch's settings and bays, so it is branch-keyed.
   * One `encounter(id)` prefix so a write invalidates the whole workspace.
   */
  emergency: {
    meta: () => branchKey('er', 'meta'),
    board: (recentHours?: number) => branchKey('er', 'board', recentHours ?? 0),
    encounter: (id: number) => branchKey('er', 'encounter', id),
    encounters: (params?: Record<string, unknown>) => branchKey('er', 'encounters', params ?? {}),
    mlc: (id: number) => branchKey('er', 'encounter', id, 'mlc'),
    kpi: (from: string, to: string) => branchKey('er', 'kpi', from, to),
    admin: (section: string) => branchKey('er', 'admin', section),
    wards: () => branchKey('er', 'wards'),
    /** The signed-in doctor's live ED pages — polled by the global popup. */
    myAlerts: () => branchKey('er', 'my-alerts'),
  },

  reports: {
    dailySummary: (date?: string) => branchKey('reports', 'daily-summary', date ?? null),
    waitTimes: () => branchKey('reports', 'wait-times'),
    /** Pharmacy sales/stock/points for a date range — one branch's till. */
    pharmacy: (from: string, to: string) => branchKey('reports', 'pharmacy', from, to),
    /** The day-end close for a date range — one branch's cash counter. */
    dayBook: (from: string, to: string) => branchKey('reports', 'day-book', from, to),
    /** The drug-substitution report for a date range, by mismatch type. */
    pharmacySubstitutions: (type: string, from: string, to: string) => branchKey('reports', 'pharmacy-substitutions', type, from, to),
  },
} as const;
